import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import NodeCard from './NodeCard';
import CanvasGuide from './CanvasGuide';
import { connect, moveNode, removeNode, toFlow } from './graphModel';
import { themeColor, useThemeMode } from '../theme';

const nodeTypes = { analyticsNode: NodeCard };

// The canvas is a view over the graph: every interaction is turned into a
// graph change and handed to the editor, which owns the state.
export default function FlowCanvas({
  graph,
  metas,
  results,
  selectedId,
  onSelectNode,
  onChange,
  workflow
}) {
  const [guideDismissed, setGuideDismissed] = useState(false);
  // Re-render on dark-mode toggle so the dot grid picks up the new token.
  useThemeMode();
  const flow = useMemo(
    () => toFlow(graph, { metas, results, selectedId }),
    [graph, metas, results, selectedId]
  );

  const onNodesChange = useCallback(
    (changes) => {
      changes.forEach((change) => {
        if (change.type === 'position' && change.position) {
          onChange(moveNode(graph, change.id, change.position));
        } else if (change.type === 'remove') {
          onChange(removeNode(graph, change.id));
        }
      });
    },
    [graph, onChange]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      const removed = changes
        .filter((change) => change.type === 'remove')
        .map((change) => change.id);
      if (!removed.length) return;
      onChange({ ...graph, edges: graph.edges.filter((edge) => !removed.includes(edge.id)) });
    },
    [graph, onChange]
  );

  const edgeReconnectSuccessful = useRef(true);

  const onReconnectStart = useCallback(() => {
    edgeReconnectSuccessful.current = false;
  }, []);

  const onReconnect = useCallback(
    (oldEdge, newConnection) => {
      edgeReconnectSuccessful.current = true;
      onChange({
        ...graph,
        edges: graph.edges.map((e) =>
          e.id === oldEdge.id
            ? { ...e, source: newConnection.source, target: newConnection.target }
            : e
        )
      });
    },
    [graph, onChange]
  );

  const onReconnectEnd = useCallback(
    (_, edge) => {
      if (!edgeReconnectSuccessful.current) {
        onChange({ ...graph, edges: graph.edges.filter((e) => e.id !== edge.id) });
      }
      edgeReconnectSuccessful.current = true;
    },
    [graph, onChange]
  );

  const onConnect = useCallback(
    (edge) => onChange(connect(graph, edge.source, edge.target)),
    [graph, onChange]
  );
  const onSelectionChange = useCallback(
    ({ nodes }) => onSelectNode(nodes.length ? nodes[0].id : null),
    [onSelectNode]
  );

  const onNodeClick = useCallback((_event, node) => onSelectNode(node.id), [onSelectNode]);

  return (
    <div className="flow-canvas">
      {!guideDismissed && (
        <CanvasGuide workflow={workflow} onComplete={() => setGuideDismissed(true)} />
      )}
      <ReactFlow
        nodes={flow.nodes}
        edges={flow.edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onReconnect={onReconnect}
        onReconnectStart={onReconnectStart}
        onReconnectEnd={onReconnectEnd}
        onSelectionChange={onSelectionChange}
        onNodeClick={onNodeClick}
        fitView
        minZoom={0.2}
        snapToGrid
        snapGrid={[16, 16]}
        panOnDrag
        zoomOnScroll
        multiSelectionKeyCode={null}
        selectionOnDrag
      >
        <Background
          gap={16}
          size={1}
          color={themeColor('--md-sys-color-outline-variant', '#777')}
        />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable className="flow-minimap" />
      </ReactFlow>
    </div>
  );
}
